import {
  OrbitControls,
  PerspectiveCamera,
  useTexture,
} from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { memo, useEffect, useRef } from "react";
import { AdditiveBlending, BackSide, Color, DoubleSide, FrontSide, LinearFilter, LinearMipMapLinearFilter, MultiplyBlending, NoBlending, NormalBlending, RepeatWrapping, SubtractiveBlending } from "three";
import AxesHelper from "../model/helper/AxesHelper";
import LightElements from "../model/helper/LightElements";
import { defaultMaterialData } from "./MapUtil";

function MaterialPreview({ mats, data, scale, onLoad }) {

  const Box = memo(({ scale, mats, data }) => {
    const materials = useTexture(mats);

    const meshRef = useRef();

    useEffect(() => {
      meshRef.current.material.needsUpdate = true;
    }, [mats, data]);

    /**
     * Enables / disables loading-screen.
     */
    useEffect(() => {
      if (materials) {
        // disable loading-screen
        onLoad(true);
      } else {
        // enable loading-screen
        onLoad(false);
      }
    }, [materials]);

    return (
      <mesh ref={meshRef} scale={[scale, scale, scale]}>
        <boxGeometry args={[1, 1, 1, 200, 200, 200]} />

        <meshPhysicalMaterial
          {...materials}
          bumpScale={getDataValue(data, "bumpScale")}
          side={getSide(data.side)}
          blending={getBlending(data.blending)}
          transparent={getDataValue(data, "transparent")}
          roughness={getDataValue(data, "roughness")}
          emissive={new Color(getDataValue(data, "emissiveColor"))}
          emissiveIntensity={getDataValue(data, "emissiveIntensity")}
          opacity={getDataValue(data, "opacity")}
          thickness={getDataValue(data, "thickness")}
          clearcoat={getDataValue(data, "clearcoat")}
          clearcoatRoughness={getDataValue(data, "clearcoatRoughness")}
          metalness={getDataValue(data, "metalness")}
          color={new Color(getDataValue(data, "color"))}
          normalScale={[getDataValue(data, "normalScaleX"), getDataValue(data, "normalScaleY")]}
          reflectivity={getDataValue(data, "reflectivity")}
          sheen={getDataValue(data, "sheen")}
          sheenRoughness={getDataValue(data, "sheenRoughness")}
          transmission={getDataValue(data, "transmission")}
          aoMapIntensity={getDataValue(data, "aoMapIntensity")}
          displacementScale={getDataValue(data, "displacementScale")}
          displacementBias={getDataValue(data, "displacementBias")}
          flatShading={getDataValue(data, "flatShading")}
          alphaTest={getDataValue(data, "alphaTest")}
          depthTest={getDataValue(data, "depthTest")}
          depthWrite={getDataValue(data, "depthWrite")}
          {...(materials.map ? {
            map: materials.map,
            mapMinFilter: LinearMipMapLinearFilter,
            mapMagFilter: LinearFilter,
            mapWrapS: RepeatWrapping,
            mapWrapT: RepeatWrapping,
            mapGenerateMipMaps: true
          } : {})}
          {...(materials.normalMap ? {
            normalMap: materials.normalMap,
            normalMinFilter: LinearMipMapLinearFilter,
            normalMagFilter: LinearFilter,
            normalWrapS: RepeatWrapping,
            normalWrapT: RepeatWrapping,
            normalGenerateMipMaps: true
          } : {})}

        //map-minFilter={LinearMipMapLinearFilter}
        //map-magFilter={LinearFilter}
        //map-wrapS={RepeatWrapping}
        //map-wrapT={RepeatWrapping}
        />

      </mesh>
    );
  }, [scale, mats, data]);

  return (
    <Canvas linear>
      <LightElements x={1} y={1} z={2} />
      <Box scale={scale} mats={mats} data={data} />
      <PerspectiveCamera makeDefault position={[0.8, 0.8, 2]} />
      <OrbitControls makeDefault enableRotate={true} minDistance={0.5} maxDistance={5} enablePan={false} />
    </Canvas>
  );
};

function getSide(side) {
  switch (side) {
    case 0:
      return FrontSide;
    case 1:
      return BackSide;
    case 2:
      return DoubleSide;
    default:
      return FrontSide;
  }
}

function getBlending(blending) {
  switch (blending) {
    case 0:
      return NoBlending;
    case 1:
      return NormalBlending;
    case 2:
      return AdditiveBlending;
    case 3:
      return SubtractiveBlending;
    case 4:
      return MultiplyBlending;
    default:
      return NormalBlending;
  }
}

function getDataValue(data, dataName) {
  return data[dataName] === undefined ? defaultMaterialData[dataName] : data[dataName];
}

export default MaterialPreview;
